/**
 * Bug Report Widget
 *
 * A floating action button that opens a modal for submitting bug reports.
 * Captures context (route, breadcrumbs) and allows screenshot attachment.
 */

import { useState, useCallback, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '../ui/Button';
import { api, type IssueSubmission, type CapturedContext } from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import { useBranchStore } from '../../stores/branchStore';
import {
  ACCEPTED_SCREENSHOT_LABEL,
  SCREENSHOT_ACCEPT_ATTR,
  describeSubmitError,
  isAcceptedScreenshotType,
} from './screenshot';

// Get breadcrumbs from error tracking service if available
function getBreadcrumbs(): unknown[] {
  try {
    // Access the breadcrumbs from sessionStorage if stored there
    const stored = sessionStorage.getItem('error_breadcrumbs');
    if (stored) return JSON.parse(stored);
  } catch {
    // Ignore
  }
  return [];
}

function getDeviceInfo(): { browser: string; deviceType: string; userAgent: string } {
  const ua = navigator.userAgent;
  let browser = 'unknown';
  let deviceType = 'desktop';

  if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Chrome')) browser = 'Chrome';
  else if (ua.includes('Safari')) browser = 'Safari';
  else if (ua.includes('Edge')) browser = 'Edge';

  if (/Mobi|Android/i.test(ua)) deviceType = 'mobile';
  else if (/Tablet|iPad/i.test(ua)) deviceType = 'tablet';

  return { browser, deviceType, userAgent: ua };
}

function getSessionId(): string {
  let sessionId = sessionStorage.getItem('error_session_id');
  if (!sessionId) {
    sessionId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem('error_session_id', sessionId);
  }
  return sessionId;
}

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function BugReportModal({ isOpen, onClose }: BugReportModalProps) {
  const { user } = useAuthStore();
  const { currentBranchId } = useBranchStore();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [issueType, setIssueType] = useState<'bug' | 'suggestion' | 'question'>('bug');
  const [severity, setSeverity] = useState<'critical' | 'high' | 'medium' | 'low'>('medium');
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const submitMutation = useMutation({
    mutationFn: async (data: IssueSubmission) => {
      const response = await api.issues.submit(data);
      if (!response.success) throw new Error(describeSubmitError(response.error));
      return response.data;
    },
    onSuccess: () => {
      toast.success('Bug report submitted successfully');
      resetForm();
      onClose();
    },
    onError: (error: Error) => {
      toast.error(`Failed to submit: ${error.message}`);
    },
  });

  const resetForm = useCallback(() => {
    setTitle('');
    setDescription('');
    setIssueType('bug');
    setSeverity('medium');
    setScreenshot(null);
  }, []);

  const handleScreenshotUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!user) {
      toast.error('Sign in to attach a screenshot');
      return;
    }

    if (!isAcceptedScreenshotType(file.type)) {
      toast.error(`Please upload a ${ACCEPTED_SCREENSHOT_LABEL} image`);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setScreenshot(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    const deviceInfo = getDeviceInfo();
    const context: CapturedContext = {
      currentRoute: window.location.pathname + window.location.search,
      sessionId: getSessionId(),
      browser: deviceInfo.browser,
      deviceType: deviceInfo.deviceType,
      appVersion: import.meta.env.VITE_APP_VERSION || 'dev',
      userAgent: deviceInfo.userAgent,
      breadcrumbs: getBreadcrumbs(),
    };

    const submission: IssueSubmission = {
      title: title.trim(),
      description: description.trim() || undefined,
      issueType,
      severity,
      screenshotUrl: user ? screenshot || undefined : undefined,
      reportedByUserId: user?.id,
      reportedByEmail: user?.email,
      reportedByRole: user?.roles?.[0],
      context,
      branchId: currentBranchId || undefined,
    };

    submitMutation.mutate(submission);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
      <div className="bg-white w-full sm:w-[520px] sm:rounded-2xl sm:max-h-[90vh] max-h-[95vh] overflow-hidden flex flex-col animate-slide-up">
        {/* Header */}
        <div className="bg-forest-800 text-white px-4 py-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold">Report an Issue</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/10 rounded transition-colors"
          >
            <span className="sr-only">Close</span>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {/* Issue Type */}
          <div>
            <label className="text-sm font-medium text-sage-700 mb-2 block">Type</label>
            <div className="flex gap-2">
              {(['bug', 'suggestion', 'question'] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setIssueType(type)}
                  className={`flex-1 py-2.5 px-3 rounded-xl border-2 transition-colors capitalize ${
                    issueType === type
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-sage-200 text-sage-600 hover:border-sage-300'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* Severity (for bugs) */}
          {issueType === 'bug' && (
            <div>
              <label className="text-sm font-medium text-sage-700 mb-2 block">Severity</label>
              <div className="flex gap-2">
                {(['low', 'medium', 'high', 'critical'] as const).map((sev) => (
                  <button
                    key={sev}
                    type="button"
                    onClick={() => setSeverity(sev)}
                    className={`flex-1 py-2 px-2 rounded-xl border-2 transition-colors capitalize text-sm ${
                      severity === sev
                        ? sev === 'critical'
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : sev === 'high'
                            ? 'border-amber-500 bg-amber-50 text-amber-700'
                            : 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-sage-200 text-sage-600 hover:border-sage-300'
                    }`}
                  >
                    {sev}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="text-sm font-medium text-sage-700 mb-1 block">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief description of the issue"
              className="w-full px-3 py-2.5 rounded-xl border-2 border-sage-200 focus:border-primary-500 focus:outline-none"
              maxLength={255}
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-medium text-sage-700 mb-1 block">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Steps to reproduce, expected behavior, etc."
              className="w-full px-3 py-2.5 rounded-xl border-2 border-sage-200 focus:border-primary-500 focus:outline-none resize-none"
              rows={4}
            />
          </div>

          {/* Screenshot */}
          <div>
            <label className="text-sm font-medium text-sage-700 mb-1 block">
              Screenshot (optional)
            </label>
            {screenshot ? (
              <div className="relative">
                <img
                  src={screenshot}
                  alt="Screenshot"
                  className="w-full rounded-xl border border-sage-200 max-h-48 object-contain bg-sage-50"
                />
                <button
                  type="button"
                  onClick={() => setScreenshot(null)}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-8 border-2 border-dashed border-sage-300 rounded-xl text-sage-500 hover:border-sage-400 hover:text-sage-600 transition-colors"
              >
                <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Click to upload screenshot
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={SCREENSHOT_ACCEPT_ATTR}
              onChange={handleScreenshotUpload}
              className="hidden"
            />
          </div>

          {/* Context Info */}
          <div className="bg-sage-50 rounded-xl p-3 text-xs text-sage-600">
            <div className="font-medium mb-1">Automatically captured:</div>
            <div>Current page: {window.location.pathname}</div>
            <div>Browser: {getDeviceInfo().browser}</div>
            <div>Device: {getDeviceInfo().deviceType}</div>
          </div>
        </form>

        {/* Footer */}
        <div className="border-t border-sage-200 px-4 py-4 flex gap-3">
          <Button
            variant="secondary"
            onClick={onClose}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={submitMutation.isPending || !title.trim()}
            className="flex-1"
          >
            {submitMutation.isPending ? 'Submitting...' : 'Submit Report'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function BugReportWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuthStore();

  // Only show for authenticated users
  if (!user) return null;

  return (
    <>
      {/* Floating Action Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-4 sm:bottom-6 sm:right-6 z-40 w-14 h-14 bg-forest-700 hover:bg-forest-800 text-white rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center group"
        aria-label="Report a bug"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        {/* Tooltip */}
        <span className="absolute right-full mr-3 px-2 py-1 bg-forest-900 text-white text-sm rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          Report a bug
        </span>
      </button>

      {/* Modal */}
      <BugReportModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
}
